const resources = {
  en: {
    myInsights: {
      title: 'My Insights',
      formDescription: 'Capture key lessons, revisit them later, and open each note for deeper AI reflection.',
      detailTitle: 'Insight details',
      detailDescription: 'Review the original note, moderation state, and follow-up reflection in one place.',
      textLabel: 'Insight',
      textPlaceholder: 'What did you notice or learn?',
      textRequired: 'Please enter text',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'tag1, tag2',
      status: {
        public: 'Public',
        private: 'Private',
        anonymous: 'Anonymous',
        pending: 'Waiting for admin approval',
        approved: 'Approved for public feed',
        rejected: 'Not approved for public feed',
      },
      sections: {
        captureTitle: 'Capture an insight',
        captureDescription: 'Write the note, decide whether to publish it, and keep the moderation choice explicit.',
        libraryTitle: 'Saved insights',
        libraryDescription: 'Browse your notes, review moderation state, and open any insight for full context.',
        detailTitle: 'Original note',
        detailDescription: 'This is the note you saved, together with its tags and current publication state.',
      },
      metrics: {
        savedInsights: 'Saved insights',
        savedInsightsMeta: 'Across your account',
        publicInsights: 'Public on this page',
        pendingReview: 'Waiting for review',
        currentPageMeta: '{{count}} visible here',
        pendingReviewMeta: 'Public notes that still need approval',
      },
      publicModerationHint: 'Public insights appear in the public feed only after an admin approves them.',
      moderation: {
        pending: 'Your public request is waiting for admin approval.',
        approved: 'This insight is approved and can appear in the public feed.',
        rejected: 'This insight is not visible publicly until you update it and request approval again.',
        noteLabel: 'Moderator note',
      },
      add: 'Add',
      update: 'Update',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      loadFailed: 'Failed to load insights',
      loadOneFailed: 'Failed to load the insight',
      updated: 'Insight updated',
      added: 'Insight added',
      saveFailed: 'Failed to save insight',
      deleted: 'Insight deleted',
      deleteFailed: 'Failed to delete insight',
      empty: 'You have not added any insights yet.',
      createdAt: 'Created',
      updatedAt: 'Updated',
      noTags: 'No tags',
      viewDetails: 'Open insight',
      notFound: 'Insight not found',
      notFoundDescription: 'We could not find the insight you were looking for.',
      forbidden: 'Access denied',
      forbiddenDescription: 'You do not have access to this insight.',
      backToList: 'Back to My Insights',
      chat: {
        title: 'AI reflection',
        description: 'Ask follow-up questions, pressure-test the note, and export the conversation when needed.',
        placeholder: 'Ask about blind spots, leverage, or next steps…',
        quickAction: 'Analyze note',
        quickPrompt: 'Analyze my note.',
        send: 'Send',
        sending: 'Sending…',
        empty: 'No chat messages yet.',
        typing: 'Typing…',
        sendError: 'Failed to send the message.',
        aiTemporarilyUnavailable: 'AI is temporarily unavailable, please try again later.',
        providerUnavailableInline: 'AI provider is temporarily unavailable. We will retry shortly.',
        accessDenied: 'Access denied: this action requires an active subscription.',
        loadError: 'Unable to load the chat history.',
        credits: '{{count}} credits left',
        creditsUnknown: 'Credits unavailable',
        creditsEmpty: 'No AI credits left.',
        copyAnswer: 'Copy answer',
        copyAnswerSuccess: 'Answer copied.',
        copyAnswerError: 'Unable to copy the answer.',
        summary: 'AI chat: {{count}} messages',
        export: {
          copy: 'Copy chat',
          download: 'Download .txt',
          copySuccess: 'Chat copied to clipboard.',
          copyError: 'Unable to copy the chat.',
          downloadError: 'Unable to download the chat.',
          copyHint: 'Paste into ChatGPT or notes.',
          downloadHint: 'Save a plain text file.',
          scrollToLatest: 'Jump to latest message',
          contextLabel: 'Context',
          userLabel: 'You',
          assistantLabel: 'AI',
        },
      },
    },
  },
  uk: {
    myInsights: {
      title: 'Мої інсайти',
      formDescription: 'Фіксуйте ключові висновки, повертайтеся до них пізніше й відкривайте кожну нотатку для глибшої AI-рефлексії.',
      detailTitle: 'Деталі інсайту',
      detailDescription: 'Переглядайте оригінальну нотатку, статус модерації та подальшу AI-рефлексію в одному місці.',
      textLabel: 'Інсайт',
      textPlaceholder: 'Що ви помітили або зрозуміли?',
      textRequired: 'Будь ласка, введіть текст',
      tagsLabel: 'Теги',
      tagsPlaceholder: 'тег1, тег2',
      status: {
        public: 'Публічний',
        private: 'Приватний',
        anonymous: 'Анонімний',
        pending: 'Очікує схвалення адміном',
        approved: 'Схвалено для публічної стрічки',
        rejected: 'Не схвалено для публічної стрічки',
      },
      sections: {
        captureTitle: 'Записати інсайт',
        captureDescription: 'Занотуйте висновок, визначте режим публікації та явно зафіксуйте вибір модерації.',
        libraryTitle: 'Збережені інсайти',
        libraryDescription: 'Переглядайте нотатки, статус модерації та відкривайте будь-який інсайт для повного контексту.',
        detailTitle: 'Оригінальна нотатка',
        detailDescription: 'Тут збережено вашу нотатку разом із тегами та поточним станом публікації.',
      },
      metrics: {
        savedInsights: 'Збережені інсайти',
        savedInsightsMeta: 'У вашому акаунті',
        publicInsights: 'Публічні на цій сторінці',
        pendingReview: 'Очікують перевірки',
        currentPageMeta: '{{count}} видно тут',
        pendingReviewMeta: 'Публічні нотатки, які ще потребують схвалення',
      },
      publicModerationHint: 'Публічні інсайти з’являються у публічній стрічці лише після ручного схвалення адміном.',
      moderation: {
        pending: 'Ваш запит на публікацію очікує схвалення адміном.',
        approved: 'Цей інсайт схвалено і він може з’являтися у публічній стрічці.',
        rejected: 'Цей інсайт не буде видимий публічно, доки ви не оновите його і не подасте знову.',
        noteLabel: 'Нотатка модератора',
      },
      add: 'Додати',
      update: 'Оновити',
      cancel: 'Скасувати',
      edit: 'Редагувати',
      delete: 'Видалити',
      loadFailed: 'Не вдалося завантажити інсайти',
      loadOneFailed: 'Не вдалося завантажити інсайт',
      updated: 'Інсайт оновлено',
      added: 'Інсайт додано',
      saveFailed: 'Не вдалося зберегти інсайт',
      deleted: 'Інсайт видалено',
      deleteFailed: 'Не вдалося видалити інсайт',
      empty: 'Інсайтів ще немає.',
      createdAt: 'Створено',
      updatedAt: 'Оновлено',
      noTags: 'Без тегів',
      viewDetails: 'Відкрити інсайт',
      notFound: 'Інсайт не знайдено',
      notFoundDescription: 'Ми не змогли знайти потрібний інсайт.',
      forbidden: 'Доступ заборонено',
      forbiddenDescription: 'У вас немає доступу до цього інсайту.',
      backToList: 'Повернутися до інсайтів',
      chat: {
        title: 'AI-рефлексія',
        description: 'Ставте уточнювальні запитання, перевіряйте нотатку на слабкі місця та експортуйте розмову за потреби.',
        placeholder: 'Запитайте про сліпі зони, важіль чи наступні кроки…',
        quickAction: 'Проаналізувати нотатку',
        quickPrompt: 'Проаналізуй мою нотатку.',
        send: 'Надіслати',
        sending: 'Надсилання…',
        empty: 'Повідомлень чату ще немає.',
        typing: 'Друкує…',
        sendError: 'Не вдалося надіслати повідомлення.',
        aiTemporarilyUnavailable: 'AI тимчасово недоступний, спробуйте пізніше.',
        providerUnavailableInline: 'AI-провайдер тимчасово недоступний. Ми спробуємо знову пізніше.',
        accessDenied: 'Доступ заборонено: ця дія потребує активної підписки.',
        loadError: 'Не вдалося завантажити історію чату.',
        credits: 'Залишилось кредитів: {{count}}',
        creditsUnknown: 'Кредити недоступні',
        creditsEmpty: 'AI-кредитів немає.',
        copyAnswer: 'Скопіювати відповідь',
        copyAnswerSuccess: 'Відповідь скопійовано.',
        copyAnswerError: 'Не вдалося скопіювати відповідь.',
        summary: 'AI-чат: {{count}} повідомлень',
        export: {
          copy: 'Скопіювати чат',
          download: 'Завантажити .txt',
          copySuccess: 'Чат скопійовано.',
          copyError: 'Не вдалося скопіювати чат.',
          downloadError: 'Не вдалося завантажити чат.',
          copyHint: 'Вставте в ChatGPT або нотатки.',
          downloadHint: 'Збережіть текстовий файл.',
          scrollToLatest: 'Перейти до останнього',
          contextLabel: 'Контекст',
          userLabel: 'Ви',
          assistantLabel: 'AI',
        },
      },
    },
  },
}

export default resources
